/**
 * Firebase Analytics wrapper (product analytics — distinct from `diag.js`,
 * which uses the same SDK for boot diagnostics only).
 *
 * Why this file exists: on native the Firebase SDK auto-reports `screen_view`
 * with whatever UIViewController / Activity is on screen. In a Capacitor app
 * that is ALWAYS `CAPBridgeViewController` (iOS) or `MainActivity` (Android),
 * so every screen in the console collapses into one meaningless row. The real
 * navigation happens in the JS router, so the JS side has to report it —
 * that's `AnalyticsScreenTracker` calling `logScreenView()` below. Automatic
 * screen reporting is switched off natively (Info.plist
 * `FirebaseAutomaticScreenReportingEnabled`, AndroidManifest
 * `google_analytics_automatic_screen_reporting_enabled`) so the two don't
 * double-count.
 *
 * Everything here is fire-and-forget: analytics must never throw into, block,
 * or otherwise influence app behaviour.
 */
import { Capacitor } from "@capacitor/core";
import { FirebaseAnalytics } from "@capacitor-firebase/analytics";

const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch (_) {
    return false;
  }
};

/**
 * The plugin's web implementation calls `getAnalytics()` on the [DEFAULT]
 * Firebase app. Our web Firebase app is created lazily (see
 * settings/firebaseConfig.js) and may not exist yet when the first screen is
 * reported, so make sure it does — and that the browser actually supports
 * analytics (Safari private mode / no IndexedDB / no cookies all fail).
 *
 * Resolved once and cached; both imports are dynamic so nothing extra is
 * pulled into the native bundle, which never takes this path.
 */
let webSupport = null;
function ensureWebAnalytics() {
  if (webSupport) return webSupport;
  webSupport = (async () => {
    const [firebaseConfig, firebaseAnalytics] = await Promise.all([
      import("../settings/firebaseConfig"),
      import("firebase/analytics"),
    ]);
    firebaseConfig.default(); // creates the [DEFAULT] app the plugin expects
    return (await firebaseAnalytics.isSupported()) === true;
  })().catch(() => false);
  return webSupport;
}

/** Run an analytics call, swallowing every failure. */
async function safeCall(fn) {
  try {
    if (!isNative() && !(await ensureWebAnalytics())) return;
    await fn();
  } catch (_) {
    /* plugin not ready, analytics unsupported, consent withheld — ignore */
  }
}

/**
 * Report the screen the user just landed on.
 *
 * Maps to a canonical `screen_view` event on all three platforms, filling
 * `firebase_screen` / `firebase_screen_class` — which is what the console's
 * "Screens" / "Pages and screens" reports read.
 *
 * @param {string} screenName  stable snake_case name, e.g. "recipe_detail"
 * @param {string} [screenClass]  the React page component, e.g. "RecipePage"
 */
export function logScreenView(screenName, screenClass) {
  if (!screenName) return;
  return safeCall(() =>
    FirebaseAnalytics.setCurrentScreen({
      screenName,
      // Always send an explicit class: left null, Android falls back to the
      // hosting Activity and we're back to "MainActivity" for everything.
      screenClassOverride: screenClass || screenName,
    })
  );
}

/**
 * Log a custom product event.
 * Firebase limits: name <= 40 chars, <= 25 params, string values <= 100 chars.
 */
export function logAnalyticsEvent(name, params) {
  if (!name) return;
  return safeCall(() =>
    FirebaseAnalytics.logEvent({ name, params: params || {} })
  );
}

/**
 * Associate the session with our internal user id, so Firebase can de-duplicate
 * the same person across devices/reinstalls in the active-user counts.
 *
 * Pass `null` on logout. Never pass an email, phone or name here — Firebase
 * policy forbids PII in analytics identifiers.
 */
export function setAnalyticsUserId(userId) {
  return safeCall(() =>
    FirebaseAnalytics.setUserId({
      userId: userId == null ? null : String(userId),
    })
  );
}

/** Set a low-cardinality user property used to segment reports (e.g. tier). */
export function setAnalyticsUserProperty(key, value) {
  if (!key) return;
  return safeCall(() =>
    FirebaseAnalytics.setUserProperty({
      key,
      value: value == null ? null : String(value),
    })
  );
}
